import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import type { ExpenseFormValues } from "@/features/expenses/schema";
import type { Expense, ExpenseListRow } from "@/features/expenses/types";

const text = (value: string | undefined) => value?.trim() || null;

function money(value: unknown) {
  const n = typeof value === "number" ? value : Number(value ?? 0);
  return Number.isNaN(n) ? 0 : n;
}

function hydrate(raw: Expense): Expense {
  return { ...raw, amount: money(raw.amount) };
}

export function expenseToForm(e: Expense): ExpenseFormValues {
  return {
    expense_date: e.expense_date,
    category: e.category,
    title: e.title,
    amount: e.amount,
    payment_method: e.payment_method,
    reference: e.reference ?? "",
    notes: e.notes ?? "",
    client_id: e.client_id ?? "",
    vendor_id: e.vendor_id ?? "",
  };
}

function normalize(values: ExpenseFormValues) {
  return {
    expense_date: values.expense_date,
    category: values.category,
    title: values.title.trim(),
    amount: values.amount,
    payment_method: values.payment_method,
    reference: text(values.reference),
    notes: text(values.notes),
    client_id: text(values.client_id),
    vendor_id: text(values.vendor_id),
  };
}

export function useExpenses() {
  return useQuery({
    queryKey: ["expenses"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<ExpenseListRow[]> => {
      const { data, error } = await supabase
        .from("expenses")
        .select("*, clients(name), vendors(name)")
        .order("expense_date", { ascending: false })
        .order("created_at", { ascending: false });
      if (error) throw error;

      return (data ?? []).map((row) => {
        const raw = row as Record<string, unknown>;
        const clients = raw.clients as { name?: string } | { name?: string }[] | null;
        const vendors = raw.vendors as { name?: string } | { name?: string }[] | null;
        const clientName = Array.isArray(clients) ? clients[0]?.name : clients?.name;
        const vendorName = Array.isArray(vendors) ? vendors[0]?.name : vendors?.name;
        const { clients: _c, vendors: _v, ...rest } = raw;
        return {
          ...hydrate(rest as unknown as Expense),
          client_name: clientName ?? null,
          vendor_name: vendorName ?? null,
        };
      });
    },
  });
}

export function useUpsertExpense() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, values }: { id?: string; values: ExpenseFormValues }) => {
      const payload = {
        ...normalize(values),
        updated_by: user?.id ?? null,
        updated_at: new Date().toISOString(),
      };
      if (id) {
        const { data, error } = await supabase
          .from("expenses")
          .update(payload)
          .eq("id", id)
          .select("*")
          .single();
        if (error) throw error;
        return hydrate(data as Expense);
      }
      const { data, error } = await supabase
        .from("expenses")
        .insert({ ...payload, created_by: user?.id ?? null })
        .select("*")
        .single();
      if (error) throw error;
      return hydrate(data as Expense);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["expenses"] });
      await queryClient.invalidateQueries({ queryKey: ["master_ledger"] });
      toast.success("Expense saved");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to save expense"),
  });
}

export function useDeleteExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("expenses").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["expenses"] });
      await queryClient.invalidateQueries({ queryKey: ["master_ledger"] });
      toast.success("Expense deleted");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to delete expense"),
  });
}
